/**
 * MeMoney / Brand Currency UI strings — aligned with app SupportedLanguage in vaiu-app i18n.
 */

export type MeMoneyLocale = 'en' | 'fr' | 'es' | 'sq' | 'ru' | 'pt' | 'el' | 'de' | 'ro' | 'sr' | 'pl';

const SUPPORTED: MeMoneyLocale[] = ['en', 'fr', 'es', 'sq', 'ru', 'pt', 'el', 'de', 'ro', 'sr', 'pl'];

/** English keys = source of truth for key names */
const EN: Record<string, string> = {
  title: 'MeMoney',
  subtitle:
    'MM$ is your always-on merchant currency. Customize how it looks on cards and in balance lists, or manage legacy brand-currency rows if your platform still exposes them.',
  tabMmMoney: 'MM$ appearance',
  tabLegacy: 'Legacy currencies',
  mmMoneyIntro:
    'Set a full-bleed token card background and a square MM$ icon. Uploads save immediately on the platform. To use a pasted URL instead, finish editing the field (click away); profile fields sync on blur.',
  mmMoneyUpload: 'Upload',
  mmMoneyUploading: 'Uploading…',
  mmMoneyUploadOkTitle: 'Image uploaded',
  mmMoneyUploadOkDesc: 'Your MM$ visuals are saved. We refreshed these fields from the server.',
  mmMoneyUploadReloadSkippedDesc:
    'Image uploaded successfully. Reload or reopen this section if the URL fields look out of date.',
  mmMoneyUploadFailedTitle: 'Upload failed',
  mmMoneyUploadFailedDesc: 'Could not upload the image. Try again or paste a URL instead.',
  mmMoneyUploadInvalidTypeTitle: 'Not an image',
  mmMoneyUploadInvalidTypeDesc: 'Choose an image file (e.g. PNG or JPEG).',
  mmMoneyUploadAriaBg: 'Upload background image',
  mmMoneyUploadAriaIcon: 'Upload MM$ icon image',
  labelMmBg: 'Background image URL',
  labelMmIcon: 'MM$ icon URL (favicon style)',
  mmMoneyPreviewIcon: 'Preview · MM$ icon',
  mmMoneyPreviewUnloadable: 'Cannot load preview — check URL, access, or CORS.',
  placeholderHttps: 'URL, /path, or data:image…',
  mmMoneyPreviewTokenCard: 'Preview · sample cash TokenCard',
  mmMoneySave: 'Save MM$ branding',
  mmMoneySavedTitle: 'Saved',
  mmMoneySavedDesc: 'MM$ branding has been updated.',
  mmMoneyInvalidUrl: 'Use a valid http(s) URL, a path starting with / ./ or ../, a data:image URL, or leave fields empty to clear.',
  mmMoneyInvalidTitle: 'Invalid URLs',
  mmMoneyLoadFailedTitle: 'Could not load branding',
  mmMoneyLoadFailedDesc: 'Try again or check your connection.',
  mmMoneyNoIntegration:
    'Connect this screen from your app with load/save handlers to edit MM$ branding (usually user profile `settings.mmMoney`).',
  mmMoneyProfileOnlyTitle: 'Manage MM$ appearance in Profile',
  mmMoneyProfileOnlyDesc:
    'Card background and MM$ icon are edited under your user profile. Merchant tools will focus on programs and governance.',
  mmMoneyProfileButton: 'Open profile',
  legacySectionTitle: 'Brand currencies',
  legacySectionDesc:
    'Optional legacy flows: separate issuer currency codes. Prefer MM$ + issuer id for new integrations.',
  createCurrency: 'Create Currency',
  hideForm: 'Hide form',
  sectionMyCurrencies: 'My currencies',
  createCardTitle: 'Create your digital currency',
  labelCurrencyName: 'Currency Name',
  placeholderCurrencyName: 'e.g., Loyalty Points',
  labelCode: 'Code (issuer-local)',
  placeholderCode: 'e.g. POINTS (4–32 chars: A–Z, 0–9, _; must start with a letter)',
  labelDisplaySymbol: 'Display symbol (optional)',
  placeholderDisplaySymbol: 'e.g. PTS, ¤, ★ — leave blank to use your code',
  hintDisplaySymbol: 'Defaults to your currency code. Any visible characters; very long values are trimmed.',
  labelDescription: 'Description',
  placeholderDescription: 'Describe your currency and its purpose...',
  labelStartingBalance: 'Your starting balance (minted to you)',
  hintStartingBalance:
    'There is no maximum supply: as issuer you can mint more anytime from Manage → Mint.',
  cancel: 'Cancel',
  createSubmit: 'Create Currency',
  creating: 'Creating...',
  circulatingSupply: 'Circulating supply',
  holders: 'Holders',
  yourBalance: 'Your Balance',
  brandIdTitle: 'Currency id',
  listSymbolLabel: 'Symbol',
  listCodeLabel: 'Code',
  manageTitle: 'Manage {{name}}',
  manageSubtitle: 'Transfer, mint, or burn tokens.',
  close: 'Close',
  tabTransfer: 'Transfer',
  tabMint: 'Mint',
  tabBurn: 'Burn',
  labelRecipient: 'Recipient',
  placeholderRecipient: 'Enter recipient address or username',
  labelAmount: 'Amount',
  mintAlert:
    'Minting creates new tokens and credits your balance. As issuer you can mint as much of your MeMoney as you need.',
  labelMintAmount: 'Amount to Mint',
  burnAlert: 'Burning destroys tokens and decreases the total supply.',
  labelBurnAmount: 'Amount to Burn',
  confirm: 'Confirm',
  processing: 'Processing...',
  status_active: 'active',
  status_paused: 'paused',
  status_delisted: 'delisted',
  toastMissingInfoTitle: 'Missing Information',
  toastMissingInfoDesc: 'Please provide a name and issuer-local code for your currency.',
  toastInvalidBalanceTitle: 'Invalid starting balance',
  toastInvalidBalanceDesc: 'Enter zero or a positive number for your initial holding.',
  toastInvalidCodeTitle: 'Invalid currency code',
  toastInvalidCodeDesc:
    'Use 4–32 characters: start with A–Z, then only letters, digits, or underscore (platform rules).',
  toastIntegerAmountTitle: 'Whole numbers only',
  toastIntegerAmountDesc: 'MeMoney amounts must be positive integers (no decimals).',
  toastCreatedTitle: 'Currency Created!',
  toastCreatedDesc: '{{name}} ({{symbol}}, {{id}}) has been created successfully.{{supplyPart}}',
  toastSupplyPart: ' Starting supply: {{supply}}.',
  toastCreateFailedTitle: 'Creation Failed',
  toastCreateFailedGenericDesc: 'Could not create currency. Please try again.',
  toastCodeTakenTitle: 'Code already in use',
  toastCodeTakenDesc: 'This issuer-local code is already in use. Try a different code.',
  toastTransferInvalidTitle: 'Invalid Transfer',
  toastTransferInvalidDesc: 'Please select a token and enter an amount.',
  toastRecipientTitle: 'Recipient required',
  toastRecipientDesc: 'Enter who should receive the transfer.',
  toastSignInTransferTitle: 'Sign in required',
  toastSignInTransferDesc: 'Transfers require a signed-in user.',
  toastSignInMintTitle: 'Sign in required',
  toastSignInMintDesc: 'Minting to your balance requires a signed-in user id.',
  toastTransferOkTitle: 'Transfer Successful',
  toastMinted: 'Minted {{amount}} {{symbol}}',
  toastBurned: 'Burned {{amount}} {{symbol}}',
  toastTransferred: 'Transferred {{amount}} {{symbol}}',
  toastTransferFailedTitle: 'Transfer Failed',
};

const FR: Record<string, string> = {
  ...EN,
  title: 'MeMoney',
  subtitle:
    'Créez et gérez vos monnaies de marque (transfert, frappe, destruction). Règles personnalisées et gouvernance : à venir.',
  createCurrency: 'Créer une monnaie',
  hideForm: 'Masquer le formulaire',
  sectionMyCurrencies: 'Mes monnaies',
  createCardTitle: 'Créer votre monnaie numérique',
  labelCurrencyName: 'Nom de la monnaie',
  placeholderCurrencyName: 'ex. Points de fidélité',
  labelCode: 'Code (local à l’émetteur)',
  labelDescription: 'Description',
  placeholderDescription: 'Décrivez votre monnaie et son usage…',
  labelStartingBalance: 'Solde de départ (émis pour vous)',
  hintStartingBalance:
    'Pas de plafond d’émission : en tant qu’émetteur vous pouvez en frapper davantage via Gérer → Frapper.',
  cancel: 'Annuler',
  createSubmit: 'Créer la monnaie',
  creating: 'Création…',
  circulatingSupply: 'Offre en circulation',
  holders: 'Détenteurs',
  yourBalance: 'Votre solde',
  brandIdTitle: 'Identifiant de la monnaie de marque',
  manageTitle: 'Gérer {{name}}',
  manageSubtitle: 'Transférer, frapper ou détruire des jetons.',
  close: 'Fermer',
  tabTransfer: 'Transférer',
  tabMint: 'Frapper',
  tabBurn: 'Détruire',
  labelRecipient: 'Destinataire',
  placeholderRecipient: 'Adresse ou nom d’utilisateur du destinataire',
  labelAmount: 'Montant',
  mintAlert:
    'La frappe crée de nouveaux jetons et crédite votre solde. En tant qu’émetteur vous pouvez frapper autant que nécessaire.',
  labelMintAmount: 'Montant à frapper',
  burnAlert: 'La destruction retire des jetons et réduit l’offre totale.',
  labelBurnAmount: 'Montant à détruire',
  confirm: 'Confirmer',
  processing: 'Traitement…',
  status_active: 'actif',
  status_paused: 'en pause',
  status_delisted: 'retiré',
  toastMissingInfoTitle: 'Informations manquantes',
  toastMissingInfoDesc: 'Indiquez un nom et un code pour votre monnaie.',
  toastInvalidBalanceTitle: 'Solde de départ invalide',
  toastInvalidBalanceDesc: 'Saisissez zéro ou un nombre positif.',
  toastInvalidCodeTitle: 'Code monnaie invalide',
  toastCreatedTitle: 'Monnaie créée !',
  toastCreatedDesc: '{{name}} ({{symbol}}, {{id}}) a été créée avec succès.{{supplyPart}}',
  toastSupplyPart: ' Offre initiale : {{supply}}.',
  toastCreateFailedTitle: 'Échec de la création',
  toastTransferInvalidTitle: 'Transfert invalide',
  toastTransferInvalidDesc: 'Sélectionnez un jeton et saisissez un montant.',
  toastRecipientTitle: 'Destinataire requis',
  toastRecipientDesc: 'Indiquez le destinataire du transfert.',
  toastSignInTransferTitle: 'Connexion requise',
  toastSignInTransferDesc: 'Les transferts nécessitent un utilisateur connecté.',
  toastSignInMintTitle: 'Connexion requise',
  toastSignInMintDesc: 'La frappe vers votre solde nécessite un identifiant utilisateur.',
  toastTransferOkTitle: 'Transfert réussi',
  toastMinted: '{{amount}} {{symbol}} frappés',
  toastBurned: '{{amount}} {{symbol}} détruits',
  toastTransferred: '{{amount}} {{symbol}} transférés',
  toastTransferFailedTitle: 'Échec du transfert',
};

const ES: Record<string, string> = {
  ...EN,
  subtitle:
    'Crea y gestiona tus monedas de marca (transferir, acuñar y quemar). Reglas y gobernanza: próximamente.',
  createCurrency: 'Crear moneda',
  hideForm: 'Ocultar formulario',
  sectionMyCurrencies: 'Mis monedas',
  createCardTitle: 'Crea tu moneda digital',
  labelCurrencyName: 'Nombre de la moneda',
  placeholderCurrencyName: 'p. ej., Puntos de fidelidad',
  labelCode: 'Código (local del emisor)',
  labelDescription: 'Descripción',
  placeholderDescription: 'Describe tu moneda y su propósito…',
  labelStartingBalance: 'Saldo inicial (acuñado para ti)',
  hintStartingBalance:
    'No hay suministro máximo: como emisor puedes acuñar más en Gestionar → Acuñar.',
  cancel: 'Cancelar',
  createSubmit: 'Crear moneda',
  creating: 'Creando…',
  circulatingSupply: 'Suministro circulante',
  holders: 'Titulares',
  yourBalance: 'Tu saldo',
  brandIdTitle: 'Id de moneda de marca',
  manageTitle: 'Gestionar {{name}}',
  manageSubtitle: 'Transferir, acuñar o quemar tokens.',
  close: 'Cerrar',
  tabTransfer: 'Transferir',
  tabMint: 'Acuñar',
  tabBurn: 'Quemar',
  labelRecipient: 'Destinatario',
  placeholderRecipient: 'Dirección o usuario del destinatario',
  labelAmount: 'Importe',
  mintAlert:
    'Acuñar crea nuevos tokens y acredita tu saldo. Como emisor puedes acuñar lo que necesites.',
  labelMintAmount: 'Importe a acuñar',
  burnAlert: 'Quemar destruye tokens y reduce el suministro total.',
  labelBurnAmount: 'Importe a quemar',
  confirm: 'Confirmar',
  processing: 'Procesando…',
  status_active: 'activo',
  status_paused: 'en pausa',
  status_delisted: 'retirado',
  toastMissingInfoTitle: 'Falta información',
  toastMissingInfoDesc: 'Indica un nombre y un código para tu moneda.',
  toastInvalidBalanceTitle: 'Saldo inicial no válido',
  toastInvalidBalanceDesc: 'Introduce cero o un número positivo.',
  toastInvalidCodeTitle: 'Código de moneda no válido',
  toastCreatedTitle: '¡Moneda creada!',
  toastCreatedDesc: '{{name}} ({{symbol}}, {{id}}) se ha creado correctamente.{{supplyPart}}',
  toastSupplyPart: ' Suministro inicial: {{supply}}.',
  toastCreateFailedTitle: 'Error al crear',
  toastTransferInvalidTitle: 'Transferencia no válida',
  toastTransferInvalidDesc: 'Selecciona un token e introduce un importe.',
  toastRecipientTitle: 'Destinatario obligatorio',
  toastRecipientDesc: 'Indica quién recibe la transferencia.',
  toastSignInTransferTitle: 'Inicio de sesión requerido',
  toastSignInTransferDesc: 'Las transferencias requieren usuario conectado.',
  toastSignInMintTitle: 'Inicio de sesión requerido',
  toastSignInMintDesc: 'Acuñar en tu saldo requiere id de usuario.',
  toastTransferOkTitle: 'Transferencia correcta',
  toastMinted: 'Acuñados {{amount}} {{symbol}}',
  toastBurned: 'Quemados {{amount}} {{symbol}}',
  toastTransferred: 'Transferidos {{amount}} {{symbol}}',
  toastTransferFailedTitle: 'Error en la transferencia',
};

const DE: Record<string, string> = {
  ...EN,
  subtitle:
    'Erstellen und verwalten Sie Ihre Markenwährungen (Transfer, Prägen, Verbrennen). Regeln und Governance folgen später.',
  createCurrency: 'Währung erstellen',
  hideForm: 'Formular ausblenden',
  sectionMyCurrencies: 'Meine Währungen',
  createCardTitle: 'Digitale Währung erstellen',
  labelCurrencyName: 'Name der Währung',
  placeholderCurrencyName: 'z. B. Treuepunkte',
  labelCode: 'Code (emittentenlokal)',
  labelDescription: 'Beschreibung',
  placeholderDescription: 'Beschreiben Sie Ihre Währung und ihren Zweck…',
  labelStartingBalance: 'Ihr Startbestand (für Sie geprägt)',
  hintStartingBalance:
    'Keine Obergrenze: Als Emittent können Sie jederzeit unter Verwalten → Prägen nachschöpfen.',
  cancel: 'Abbrechen',
  createSubmit: 'Währung erstellen',
  creating: 'Wird erstellt…',
  circulatingSupply: 'Umlaufende Menge',
  holders: 'Inhaber',
  yourBalance: 'Ihr Saldo',
  brandIdTitle: 'Markenwährungs-ID',
  manageTitle: '{{name}} verwalten',
  manageSubtitle: 'Token transferieren, prägen oder verbrennen.',
  close: 'Schließen',
  tabTransfer: 'Transfer',
  tabMint: 'Prägen',
  tabBurn: 'Verbrennen',
  labelRecipient: 'Empfänger',
  placeholderRecipient: 'Empfängeradresse oder Benutzername',
  labelAmount: 'Betrag',
  mintAlert:
    'Prägen schafft neue Token und gutschreibt Ihrem Saldo. Als Emittent können Sie beliebig prägen.',
  labelMintAmount: 'Zu prägender Betrag',
  burnAlert: 'Verbrennen zerstört Token und verringert die Gesamtmenge.',
  labelBurnAmount: 'Zu verbrennender Betrag',
  confirm: 'Bestätigen',
  processing: 'Wird verarbeitet…',
  status_active: 'aktiv',
  status_paused: 'pausiert',
  status_delisted: 'delisted',
  toastMissingInfoTitle: 'Angaben fehlen',
  toastMissingInfoDesc: 'Bitte Name und Code für Ihre Währung angeben.',
  toastInvalidBalanceTitle: 'Ungültiger Startbestand',
  toastInvalidBalanceDesc: 'Geben Sie null oder eine positive Zahl ein.',
  toastInvalidCodeTitle: 'Ungültiger Währungscode',
  toastCreatedTitle: 'Währung erstellt!',
  toastCreatedDesc: '{{name}} ({{symbol}}, {{id}}) wurde erfolgreich erstellt.{{supplyPart}}',
  toastSupplyPart: ' Startmenge: {{supply}}.',
  toastCreateFailedTitle: 'Erstellung fehlgeschlagen',
  toastTransferInvalidTitle: 'Ungültiger Transfer',
  toastTransferInvalidDesc: 'Wählen Sie einen Token und geben Sie einen Betrag ein.',
  toastRecipientTitle: 'Empfänger erforderlich',
  toastRecipientDesc: 'Geben Sie den Empfänger des Transfers an.',
  toastSignInTransferTitle: 'Anmeldung erforderlich',
  toastSignInTransferDesc: 'Transfers erfordern einen angemeldeten Benutzer.',
  toastSignInMintTitle: 'Anmeldung erforderlich',
  toastSignInMintDesc: 'Prägen auf Ihr Saldo erfordert eine Benutzer-ID.',
  toastTransferOkTitle: 'Transfer erfolgreich',
  toastMinted: '{{amount}} {{symbol}} geprägt',
  toastBurned: '{{amount}} {{symbol}} verbrannt',
  toastTransferred: '{{amount}} {{symbol}} transferiert',
  toastTransferFailedTitle: 'Transfer fehlgeschlagen',
};

const PT: Record<string, string> = {
  ...EN,
  subtitle:
    'Crie e gerencie suas moedas de marca (transferir, cunhar e queimar). Regras e governança em breve.',
  createCurrency: 'Criar moeda',
  hideForm: 'Ocultar formulário',
  sectionMyCurrencies: 'Minhas moedas',
  createCardTitle: 'Crie sua moeda digital',
  labelCurrencyName: 'Nome da moeda',
  placeholderCurrencyName: 'ex., Pontos de fidelidade',
  labelCode: 'Código (local do emissor)',
  labelDescription: 'Descrição',
  placeholderDescription: 'Descreva sua moeda e o propósito…',
  labelStartingBalance: 'Saldo inicial (cunhado para você)',
  hintStartingBalance:
    'Sem oferta máxima: como emissor você pode cunhar mais em Gerenciar → Cunhar.',
  cancel: 'Cancelar',
  createSubmit: 'Criar moeda',
  creating: 'Criando…',
  circulatingSupply: 'Oferta circulante',
  holders: 'Detentores',
  yourBalance: 'Seu saldo',
  brandIdTitle: 'Id da moeda de marca',
  manageTitle: 'Gerenciar {{name}}',
  manageSubtitle: 'Transferir, cunhar ou queimar tokens.',
  close: 'Fechar',
  tabTransfer: 'Transferir',
  tabMint: 'Cunhar',
  tabBurn: 'Queimar',
  labelRecipient: 'Destinatário',
  placeholderRecipient: 'Endereço ou nome de usuário do destinatário',
  labelAmount: 'Valor',
  mintAlert:
    'Cunhar cria novos tokens e credita seu saldo. Como emissor você pode cunhar o quanto precisar.',
  labelMintAmount: 'Valor a cunhar',
  burnAlert: 'Queimar destrói tokens e reduz a oferta total.',
  labelBurnAmount: 'Valor a queimar',
  confirm: 'Confirmar',
  processing: 'Processando…',
  status_active: 'ativo',
  status_paused: 'pausado',
  status_delisted: 'retirado',
  toastMissingInfoTitle: 'Informações incompletas',
  toastMissingInfoDesc: 'Informe nome e código da moeda.',
  toastInvalidBalanceTitle: 'Saldo inicial inválido',
  toastInvalidBalanceDesc: 'Digite zero ou um número positivo.',
  toastInvalidCodeTitle: 'Código de moeda inválido',
  toastCreatedTitle: 'Moeda criada!',
  toastCreatedDesc: '{{name}} ({{symbol}}, {{id}}) foi criada com sucesso.{{supplyPart}}',
  toastSupplyPart: ' Oferta inicial: {{supply}}.',
  toastCreateFailedTitle: 'Falha ao criar',
  toastTransferInvalidTitle: 'Transferência inválida',
  toastTransferInvalidDesc: 'Selecione um token e informe um valor.',
  toastRecipientTitle: 'Destinatário obrigatório',
  toastRecipientDesc: 'Informe quem receberá a transferência.',
  toastSignInTransferTitle: 'Login necessário',
  toastSignInTransferDesc: 'Transferências exigem usuário autenticado.',
  toastSignInMintTitle: 'Login necessário',
  toastSignInMintDesc: 'Cunhar no seu saldo exige id de usuário.',
  toastTransferOkTitle: 'Transferência bem-sucedida',
  toastMinted: 'Cunhados {{amount}} {{symbol}}',
  toastBurned: 'Queimados {{amount}} {{symbol}}',
  toastTransferred: 'Transferidos {{amount}} {{symbol}}',
  toastTransferFailedTitle: 'Falha na transferência',
};

const EL: Record<string, string> = {
  ...EN,
  subtitle:
    'Δημιουργήστε και διαχειριστείτε τα εμπορικά σας νομίσματα (μεταφορά, κοπή, καταστροφή). Κανόνες και διακυβέρνηση: σύντομα.',
  createCurrency: 'Δημιουργία νομίσματος',
  hideForm: 'Απόκρυψη φόρμας',
  sectionMyCurrencies: 'Τα νομίσματά μου',
  createCardTitle: 'Δημιουργήστε το ψηφιακό σας νόμισμα',
  labelCurrencyName: 'Όνομα νομίσματος',
  placeholderCurrencyName: 'π.χ. Πόντοι πίστης',
  labelCode: 'Κωδικός (τοπικός εκδότη)',
  labelDescription: 'Περιγραφή',
  placeholderDescription: 'Περιγράψτε το νόμισμα και τον σκοπό του…',
  labelStartingBalance: 'Αρχικό υπόλοιπο (κοπή για εσάς)',
  hintStartingBalance:
    'Χωρίς μέγιστη προσφορά: ως εκδότης μπορείτε να κόψετε περισσότερα από Διαχείριση → Κοπή.',
  cancel: 'Ακύρωση',
  createSubmit: 'Δημιουργία νομίσματος',
  creating: 'Δημιουργία…',
  circulatingSupply: 'Κυκλοφορούσα προσφορά',
  holders: 'Κάτοχοι',
  yourBalance: 'Το υπόλοιπό σας',
  brandIdTitle: 'Αναγνωριστικό εμπορικού νομίσματος',
  manageTitle: 'Διαχείριση {{name}}',
  manageSubtitle: 'Μεταφορά, κοπή ή καταστροφή token.',
  close: 'Κλείσιμο',
  tabTransfer: 'Μεταφορά',
  tabMint: 'Κοπή',
  tabBurn: 'Καταστροφή',
  labelRecipient: 'Παραλήπτης',
  placeholderRecipient: 'Διεύθυνση ή όνομα χρήστη παραλήπτη',
  labelAmount: 'Ποσό',
  mintAlert:
    'Η κοπή δημιουργεί νέα token και πιστώνει το υπόλοιπό σας. Ως εκδότης μπορείτε να κόψετε όσα χρειάζεστε.',
  labelMintAmount: 'Ποσό προς κοπή',
  burnAlert: 'Η καταστροφή καταστρέφει token και μειώνει τη συνολική προσφορά.',
  labelBurnAmount: 'Ποσό προς καταστροφή',
  confirm: 'Επιβεβαίωση',
  processing: 'Επεξεργασία…',
  status_active: 'ενεργό',
  status_paused: 'σε παύση',
  status_delisted: 'αποσύρθηκε',
  toastMissingInfoTitle: 'Λείπουν πληροφορίες',
  toastMissingInfoDesc: 'Δώστε όνομα και κωδικό για το νόμισμα.',
  toastInvalidBalanceTitle: 'Μη έγκυρο αρχικό υπόλοιπο',
  toastInvalidBalanceDesc: 'Εισάγετε μηδέν ή θετικό αριθμό.',
  toastInvalidCodeTitle: 'Μη έγκυρος κωδικός νομίσματος',
  toastCreatedTitle: 'Το νόμισμα δημιουργήθηκε!',
  toastCreatedDesc: 'Το {{name}} ({{symbol}}, {{id}}) δημιουργήθηκε επιτυχώς.{{supplyPart}}',
  toastSupplyPart: ' Αρχική προσφορά: {{supply}}.',
  toastCreateFailedTitle: 'Αποτυχία δημιουργίας',
  toastTransferInvalidTitle: 'Μη έγκυρη μεταφορά',
  toastTransferInvalidDesc: 'Επιλέξτε token και εισάγετε ποσό.',
  toastRecipientTitle: 'Απαιτείται παραλήπτης',
  toastRecipientDesc: 'Ποιος θα λάβει τη μεταφορά;',
  toastSignInTransferTitle: 'Απαιτείται σύνδεση',
  toastSignInTransferDesc: 'Οι μεταφορές απαιτούν συνδεδεμένο χρήστη.',
  toastSignInMintTitle: 'Απαιτείται σύνδεση',
  toastSignInMintDesc: 'Η κοπή στο υπόλοιπό σας απαιτεί αναγνωριστικό χρήστη.',
  toastTransferOkTitle: 'Επιτυχής μεταφορά',
  toastMinted: 'Εκόπηκαν {{amount}} {{symbol}}',
  toastBurned: 'Καταστράφηκαν {{amount}} {{symbol}}',
  toastTransferred: 'Μεταφέρθηκαν {{amount}} {{symbol}}',
  toastTransferFailedTitle: 'Αποτυχία μεταφοράς',
};

const RO: Record<string, string> = {
  ...EN,
  subtitle:
    'Creați și gestionați monedele de marcă (transfer, emitere, distrugere). Reguli și guvernanță vor urma.',
  createCurrency: 'Creează monedă',
  hideForm: 'Ascunde formularul',
  sectionMyCurrencies: 'Monedele mele',
  createCardTitle: 'Creează moneda ta digitală',
  labelCurrencyName: 'Numele monedei',
  placeholderCurrencyName: 'ex., Puncte de loialitate',
  labelCode: 'Cod (local emitent)',
  labelDescription: 'Descriere',
  placeholderDescription: 'Descrie moneda și scopul ei…',
  labelStartingBalance: 'Sold inițial (emis pentru tine)',
  hintStartingBalance:
    'Fără ofertă maximă: ca emitent poți emite mai mult din Gestionare → Emite.',
  cancel: 'Anulează',
  createSubmit: 'Creează moneda',
  creating: 'Se creează…',
  circulatingSupply: 'Ofertă în circulație',
  holders: 'Deținători',
  yourBalance: 'Soldul tău',
  brandIdTitle: 'Id monedă de marcă',
  manageTitle: 'Gestionează {{name}}',
  manageSubtitle: 'Transferă, emite sau distruge tokenuri.',
  close: 'Închide',
  tabTransfer: 'Transfer',
  tabMint: 'Emite',
  tabBurn: 'Distruge',
  labelRecipient: 'Destinatar',
  placeholderRecipient: 'Adresa sau numele destinatarului',
  labelAmount: 'Sumă',
  mintAlert:
    'Emiterea creează tokenuri noi și îți creditează soldul. Ca emitent poți emite cât ai nevoie.',
  labelMintAmount: 'Sumă de emis',
  burnAlert: 'Distrugerea elimină tokenuri și reduce oferta totală.',
  labelBurnAmount: 'Sumă de distrus',
  confirm: 'Confirmă',
  processing: 'Se procesează…',
  status_active: 'activ',
  status_paused: 'întrerupt',
  status_delisted: 'retras',
  toastMissingInfoTitle: 'Informații lipsă',
  toastMissingInfoDesc: 'Introdu nume și cod pentru monedă.',
  toastInvalidBalanceTitle: 'Sold inițial invalid',
  toastInvalidBalanceDesc: 'Introdu zero sau un număr pozitiv.',
  toastInvalidCodeTitle: 'Cod monedă invalid',
  toastCreatedTitle: 'Monedă creată!',
  toastCreatedDesc: '{{name}} ({{symbol}}, {{id}}) a fost creată cu succes.{{supplyPart}}',
  toastSupplyPart: ' Ofertă inițială: {{supply}}.',
  toastCreateFailedTitle: 'Creare eșuată',
  toastTransferInvalidTitle: 'Transfer invalid',
  toastTransferInvalidDesc: 'Selectează un token și introdu o sumă.',
  toastRecipientTitle: 'Destinatar necesar',
  toastRecipientDesc: 'Introdu destinatarul transferului.',
  toastSignInTransferTitle: 'Autentificare necesară',
  toastSignInTransferDesc: 'Transferurile necesită utilizator autentificat.',
  toastSignInMintTitle: 'Autentificare necesară',
  toastSignInMintDesc: 'Emiterea în sold necesită id utilizator.',
  toastTransferOkTitle: 'Transfer reușit',
  toastMinted: 'Emise {{amount}} {{symbol}}',
  toastBurned: 'Distruse {{amount}} {{symbol}}',
  toastTransferred: 'Transferate {{amount}} {{symbol}}',
  toastTransferFailedTitle: 'Transfer eșuat',
};

const PL: Record<string, string> = {
  ...EN,
  subtitle:
    'Twórz i zarządzaj walutami markowymi (transfer, bicie, niszczenie). Własne zasady i zarządzanie — wkrótce.',
  createCurrency: 'Utwórz walutę',
  hideForm: 'Ukryj formularz',
  sectionMyCurrencies: 'Moje waluty',
  createCardTitle: 'Utwórz swoją walutę cyfrową',
  labelCurrencyName: 'Nazwa waluty',
  placeholderCurrencyName: 'np. Punkty lojalnościowe',
  labelCode: 'Kod (lokalny emitenta)',
  labelDescription: 'Opis',
  placeholderDescription: 'Opisz walutę i jej cel…',
  labelStartingBalance: 'Saldo początkowe (wybite dla Ciebie)',
  hintStartingBalance:
    'Brak limitu podaży: jako emitent możesz bić więcej w Zarządzaj → Bicie.',
  cancel: 'Anuluj',
  createSubmit: 'Utwórz walutę',
  creating: 'Tworzenie…',
  circulatingSupply: 'Podaż w obiegu',
  holders: 'Posiadacze',
  yourBalance: 'Twoje saldo',
  brandIdTitle: 'Id waluty markowej',
  manageTitle: 'Zarządzaj {{name}}',
  manageSubtitle: 'Transferuj, bij lub niszcz tokeny.',
  close: 'Zamknij',
  tabTransfer: 'Transfer',
  tabMint: 'Bicie',
  tabBurn: 'Niszczenie',
  labelRecipient: 'Odbiorca',
  placeholderRecipient: 'Adres lub nazwa odbiorcy',
  labelAmount: 'Kwota',
  mintAlert:
    'Bicie tworzy nowe tokeny i zwiększa Twoje saldo. Jako emitent możesz bić tyle, ile potrzebujesz.',
  labelMintAmount: 'Kwota do bicia',
  burnAlert: 'Niszczenie usuwa tokeny i zmniejsza całkowitą podaż.',
  labelBurnAmount: 'Kwota do zniszczenia',
  confirm: 'Potwierdź',
  processing: 'Przetwarzanie…',
  status_active: 'aktywna',
  status_paused: 'wstrzymana',
  status_delisted: 'wycofana',
  toastMissingInfoTitle: 'Brak informacji',
  toastMissingInfoDesc: 'Podaj nazwę i kod waluty.',
  toastInvalidBalanceTitle: 'Nieprawidłowe saldo początkowe',
  toastInvalidBalanceDesc: 'Wpisz zero lub liczbę dodatnią.',
  toastInvalidCodeTitle: 'Nieprawidłowy kod waluty',
  toastCreatedTitle: 'Waluta utworzona!',
  toastCreatedDesc: '{{name}} ({{symbol}}, {{id}}) została utworzona pomyślnie.{{supplyPart}}',
  toastSupplyPart: ' Początkowa podaż: {{supply}}.',
  toastCreateFailedTitle: 'Tworzenie nie powiodło się',
  toastTransferInvalidTitle: 'Nieprawidłowy transfer',
  toastTransferInvalidDesc: 'Wybierz token i podaj kwotę.',
  toastRecipientTitle: 'Wymagany odbiorca',
  toastRecipientDesc: 'Podaj odbiorcę transferu.',
  toastSignInTransferTitle: 'Wymagane logowanie',
  toastSignInTransferDesc: 'Transfery wymagają zalogowanego użytkownika.',
  toastSignInMintTitle: 'Wymagane logowanie',
  toastSignInMintDesc: 'Bicie na Twoje saldo wymaga id użytkownika.',
  toastTransferOkTitle: 'Transfer udany',
  toastMinted: 'Wybite {{amount}} {{symbol}}',
  toastBurned: 'Zniszczone {{amount}} {{symbol}}',
  toastTransferred: 'Przelane {{amount}} {{symbol}}',
  toastTransferFailedTitle: 'Transfer nieudany',
};

/** Albanian */
const SQ: Record<string, string> = {
  ...EN,
  subtitle:
    'Krijoni dhe menaxhoni monedhat e markës (transfer, shtypje, djegie). Rregullat dhe qeverisja vijnë më vonë.',
  createCurrency: 'Krijo monedhë',
  hideForm: 'Fshih formularin',
  sectionMyCurrencies: 'Monedhat e mia',
  createCardTitle: 'Krijo monedhën tënde dixhitale',
  labelCurrencyName: 'Emri i monedhës',
  labelCode: 'Kodi (lokal i emetuesit)',
  labelDescription: 'Përshkrimi',
  labelStartingBalance: 'Bilanci fillestar',
  cancel: 'Anulo',
  createSubmit: 'Krijo monedhën',
  creating: 'Duke krijuar…',
  circulatingSupply: 'Oferta në qarkullim',
  holders: 'Mbajtësit',
  yourBalance: 'Bilanci juaj',
  manageTitle: 'Menaxho {{name}}',
  manageSubtitle: 'Transfero, shtyp ose digj tokenët.',
  close: 'Mbyll',
  tabTransfer: 'Transfero',
  tabMint: 'Shtyp',
  tabBurn: 'Digj',
  labelRecipient: 'Marrësi',
  labelAmount: 'Shuma',
  confirm: 'Konfirmo',
  processing: 'Duke përpunuar…',
};

/** Russian */
const RU: Record<string, string> = {
  ...EN,
  subtitle:
    'Создавайте и управляйте бренд-валютами (перевод, чеканка, сжигание). Свои правила и управление — позже.',
  createCurrency: 'Создать валюту',
  hideForm: 'Скрыть форму',
  sectionMyCurrencies: 'Мои валюты',
  createCardTitle: 'Создайте свою цифровую валюту',
  labelCurrencyName: 'Название валюты',
  labelCode: 'Код (локальный эмитента)',
  labelDescription: 'Описание',
  labelStartingBalance: 'Начальный баланс',
  cancel: 'Отмена',
  createSubmit: 'Создать валюту',
  creating: 'Создание…',
  circulatingSupply: 'Оборотное предложение',
  holders: 'Держатели',
  yourBalance: 'Ваш баланс',
  manageTitle: 'Управление {{name}}',
  manageSubtitle: 'Перевод, чеканка или сжигание.',
  close: 'Закрыть',
  tabTransfer: 'Перевод',
  tabMint: 'Чеканка',
  tabBurn: 'Сжигание',
  labelRecipient: 'Получатель',
  labelAmount: 'Сумма',
  confirm: 'Подтвердить',
  processing: 'Обработка…',
};

/** Serbian Cyrillic */
const SR: Record<string, string> = {
  ...EN,
  subtitle:
    'Креирајте и управљајте бренд валутама (пренос, ковање, спаљивање). Правила и управљање — ускоро.',
  createCurrency: 'Креирај валуту',
  hideForm: 'Сакриј образац',
  sectionMyCurrencies: 'Моје валуте',
  createCardTitle: 'Креирајте своју дигиталну валуту',
  labelCurrencyName: 'Назив валуте',
  labelCode: 'Код (локални емитента)',
  labelDescription: 'Опис',
  labelStartingBalance: 'Почетни салдо',
  cancel: 'Откажи',
  createSubmit: 'Креирај валуту',
  creating: 'Креирање…',
  circulatingSupply: 'Оптицајна понуда',
  holders: 'Држачи',
  yourBalance: 'Ваш салдо',
  manageTitle: 'Управљај {{name}}',
  manageSubtitle: 'Пренеси, куј или спали токене.',
  close: 'Затвори',
  tabTransfer: 'Пренос',
  tabMint: 'Ковање',
  tabBurn: 'Спаљивање',
  labelRecipient: 'Прималац',
  labelAmount: 'Износ',
  confirm: 'Потврди',
  processing: 'Обрада…',
};

export const ME_MONEY_LOCALES: Record<MeMoneyLocale, Record<string, string>> = {
  en: EN,
  fr: FR,
  es: ES,
  de: DE,
  pt: PT,
  el: EL,
  ro: RO,
  pl: PL,
  sq: SQ,
  ru: RU,
  sr: SR,
};

function isMeMoneyLocale(x: string): x is MeMoneyLocale {
  return SUPPORTED.includes(x as MeMoneyLocale);
}

export function meMoneyT(
  locale: string | undefined,
  key: string,
  params?: Record<string, string | number>
): string {
  const code = locale && isMeMoneyLocale(locale) ? locale : 'en';
  const table = ME_MONEY_LOCALES[code];
  let s = table[key] ?? EN[key] ?? key;
  if (params) {
    for (const [k, v] of Object.entries(params)) {
      s = s.split(`{{${k}}}`).join(String(v));
    }
  }
  return s;
}
